import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import type { UserAccessPhase } from "@/features/identity-hub/lib/merge-users-access";
import { RemoveAllRolesPanel } from "./RemoveAllRolesPanel";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";

interface RemoveAllRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: string[];
  accessByUser: Map<string, UserAccessEntry[]>;
  accessPhaseByUser: Map<string, UserAccessPhase>;
  /** Nexus id of the logged-in user; never sent for removal. */
  loggedInUserId: string | null;
  userNames: Map<string, string>;
  onRemoveAll: (userIds: string[]) => void;
  isPending: boolean;
}

export function RemoveAllRolesDialog({
  open, onOpenChange, selectedUserIds, accessByUser, accessPhaseByUser,
  loggedInUserId, userNames, onRemoveAll, isPending,
}: RemoveAllRolesDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" {...PREVENT_DISMISS_PROPS}>
        <DialogHeader>
          <DialogTitle>{t("iam.dashboard.people.assignFixed.removeAllTitle", "Remove all roles")}</DialogTitle>
          <DialogDescription>
            {t(
              "iam.dashboard.people.assignFixed.removeAllDescription",
              "Removes every role in every organization. You and Adge staff are never included.",
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Mounted only while open so the confirm step starts over on every open. */}
        {open && (
          <div className="flex flex-col gap-3 py-1">
            <RemoveAllRolesPanel
              selectedUserIds={selectedUserIds}
              accessByUser={accessByUser}
              accessPhaseByUser={accessPhaseByUser}
              loggedInUserId={loggedInUserId}
              userNames={userNames}
              onRemoveAll={onRemoveAll}
              onCancel={() => onOpenChange(false)}
              isPending={isPending}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
